// primary/primaryDark/light são a identidade visual por tenant — aplicarSiteConfig()
// (App.js) escreve --color-primary/--color-primary-dark/--color-fundo-app a partir da
// Personalização de cada tenant; os valores hex aqui são só o fallback antes de carregar.
const colors = {
  primary: 'var(--color-primary, #0f5a30ff)',
  primaryDark: 'var(--color-primary-dark, #407e1fff)',
  primaryLight: '#e8f5e9',
  secondary: 'var(--color-primary-dark, #65a24bff)',
  success: '#10b981',
  warning: '#f59e0b',
  warningLight: '#fef3c7',
  warningDark: '#92400e',
  danger: '#ef4444',
  error: '#ef4444',
  info: '#3b82f6',
  light: 'var(--color-fundo-app, #f8fafc)',
  dark: '#1e293b',
  gray: '#64748b',
  grayLight: '#f1f5f9',
  white: '#ffffff'
};

const theme = {
  colors,
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.5rem',
    lg: '1rem',
    full: '9999px'
  },
  fontFamily: {
    // fallback antes de aplicarFonte() rodar (utils/siteBranding.js) — depois
    // disso, --font-family passa a valer o que foi escolhido em Personalização.
    sans: "var(--font-family, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif)",
    heading: "var(--font-family, 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif)"
  }
};

const styles = {
  // ============ ESTILOS GERAIS ============
  appContainer: {
    minHeight: '100vh',
    backgroundColor: colors.light,
    fontFamily: theme.fontFamily.sans
  },

  // ============ HEADER ============
  header: {
    backgroundColor: colors.white,
    padding: '1rem 2rem',
    boxShadow: theme.shadows.md,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 100
  },

  headerLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    color: colors.primary,
    textDecoration: 'none'
  },

  headerLogoIcon: {
    fontSize: '1.875rem'
  },

  headerLogoText: {
    fontSize: '1.5rem',
    fontWeight: '700',
    fontFamily: theme.fontFamily.heading
  },

  headerUserInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },

  headerAvatar: {
    width: '2.5rem',
    height: '2.5rem',
    borderRadius: '50%',
    backgroundColor: colors.primary,
    color: colors.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600'
  },

  headerBadge: {
    padding: '0.25rem 0.75rem',
    borderRadius: theme.borderRadius.full,
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase'
  },

  adminBadge: {
    backgroundColor: '#fef3c7',
    color: '#92400e'
  },

  userBadge: {
    backgroundColor: '#dbeafe',
    color: '#1e40af'
  },

  // ============ CARD ============
  card: {
    backgroundColor: colors.white,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.md,
    padding: '1.5rem',
    transition: 'all 0.3s ease'
  },

  cardHover: {
    ':hover': {
      boxShadow: theme.shadows.lg,
      transform: 'translateY(-2px)'
    }
  },

  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  },

  cardTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: colors.dark,
    margin: 0
  },

  // ============ BOTÕES ============
  button: {
    padding: '0.625rem 1.25rem',
    borderRadius: theme.borderRadius.md,
    border: 'none',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem'
  },

  buttonPrimary: {
    backgroundColor: colors.primary,
    color: colors.white
  },

  buttonSuccess: {
    backgroundColor: colors.success,
    color: colors.white
  },

  buttonDanger: {
    backgroundColor: colors.danger,
    color: colors.white
  },

  buttonGhost: {
    backgroundColor: colors.success,
    color: colors.secondary
  },

  buttonOutline: {
    backgroundColor: 'transparent',
    color: colors.primary,
    border: `2px solid ${colors.primary}`
  },

  buttonWarning: {
    backgroundColor: colors.warning,
    color: '#ffffff'
  },

  buttonSmall: {
    padding: '0.375rem 0.75rem',
    fontSize: '0.75rem'
  },

  buttonIcon: {
    padding: '0.5rem',
    width: '2rem',
    height: '2rem'
  },

  // ============ FORMULÁRIOS ============
  formGroup: {
    marginBottom: '1.25rem'
  },

  formLabel: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '500',
    color: colors.dark
  },

  formInput: {
    width: '100%',
    padding: '0.75rem 1rem',
    borderRadius: theme.borderRadius.md,
    border: `1px solid #e2e8f0`,
    fontSize: '16px',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
  },

  formSelect: {
    width: '100%',
    padding: '0.75rem 1rem',
    borderRadius: theme.borderRadius.md,
    border: `1px solid #e2e8f0`,
    fontSize: '16px',
    backgroundColor: colors.white,
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
  },

  formError: {
    marginTop: '0.5rem',
    color: colors.danger,
    fontSize: '0.875rem'
  },

  // ============ LOADING & ESTADOS ============
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },

  spinner: {
    width: '3rem',
    height: '3rem',
    border: `3px solid ${colors.grayLight}`,
    borderTopColor: colors.primary,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },

  // ============ GRADE SEMANAL ============
  gradeContainer: {
    backgroundColor: colors.white,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.md,
    overflow: 'hidden'
  },

  gradeHeader: {
    backgroundColor: colors.primary,
    color: colors.white,
    padding: '1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  gradeWeekNav: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },

  gradeTable: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '800px'
  },

  gradeTableHeader: {
    backgroundColor: colors.grayLight,
    borderBottom: `2px solid #e2e8f0`
  },

  gradeTableHeaderCell: {
    padding: '1rem',
    textAlign: 'center',
    fontWeight: '600',
    color: colors.dark,
    borderRight: `1px solid #e2e8f0`
  },

  gradeTableTimeCell: {
    padding: '1rem',
    textAlign: 'center',
    fontWeight: '600',
    backgroundColor: colors.grayLight,
    borderRight: `1px solid #e2e8f0`,
    borderBottom: `1px solid #e2e8f0`
  },

  gradeTableCell: {
    padding: '0.5rem',
    verticalAlign: 'top',
    borderRight: `1px solid #e2e8f0`,
    borderBottom: `1px solid #e2e8f0`,
    minWidth: '160px'
  },

  // ============ VAGA ============
  vagaContainer: {
    padding: '0.75rem',
    margin: '0.25rem 0',
    borderRadius: theme.borderRadius.md,
    border: `1px solid #e2e8f0`,
    transition: 'all 0.2s ease'
  },

  vagaAvailable: {
    backgroundColor: '#f0f9ff',
    borderColor: '#bae6fd'
  },

  vagaBooked: {
    backgroundColor: '#f0fdf4',
    borderColor: '#bbf7d0'
  },

  vagaFixed: {
    backgroundColor: '#e8eff0ff',
    borderColor: '#afc1c5ff'
  },

  vagaDraft: {
    backgroundColor: '#c4c9c8ff',
    borderColor: '#9da0a5ff',
    opacity: 0.7
  },

  vagaLocked: {
    backgroundColor: '#f16c6cff',
    borderColor: '#fecaca'
  },

  vagaTime: {
    fontSize: '0.75rem',
    color: colors.gray,
    marginBottom: '0.25rem'
  },

  vagaStatus: {
    display: 'inline-block',
    padding: '0.125rem 0.5rem',
    borderRadius: theme.borderRadius.full,
    fontSize: '0.625rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: '0.5rem'
  },

  statusAvailable: {
    backgroundColor: '#d1fae5',
    color: '#065f46'
  },

  statusBooked: {
    backgroundColor: '#dbeafe',
    color: '#1e40af'
  },

  statusFixed: {
    backgroundColor: '#fef3c7',
    color: '#92400e'
  },

  statusWaiting: {
    backgroundColor: '#fce7f3',
    color: '#9d174d'
  },

  // ============ MODAL ============
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem'
  },

  modalContent: {
    backgroundColor: colors.white,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.xl,
    maxWidth: '500px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto'
  },

  modalHeader: {
    padding: '1.5rem 1.5rem 0.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: colors.dark,
    margin: 0
  },

  modalBody: {
    padding: '0 1.5rem 1.5rem'
  },

  modalFooter: {
    padding: '1rem 1.5rem',
    backgroundColor: colors.grayLight,
    borderBottomLeftRadius: theme.borderRadius.lg,
    borderBottomRightRadius: theme.borderRadius.lg,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem'
  },

  // ============ TABS ============
  tabsContainer: {
    display: 'flex',
    borderBottom: `2px solid #e2e8f0`,
    marginBottom: '1.5rem'
  },

  tabButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: colors.gray,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative'
  },

  // ============ BADGES ============
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.25rem 0.75rem',
    borderRadius: theme.borderRadius.full,
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase'
  },

  badgeSuccess: {
    backgroundColor: '#d1fae5',
    color: '#065f46'
  },

  badgeWarning: {
    backgroundColor: '#fef3c7',
    color: '#92400e'
  },

  badgeDanger: {
    backgroundColor: '#fee2e2',
    color: '#991b1b'
  },

  badgeInfo: {
    backgroundColor: '#dbeafe',
    color: '#1e40af'
  },

  badgePrimary: {
    backgroundColor: '#dcfce7',
    color: '#166534'
  },

  // ============ ALERTAS ============
  alert: {
    padding: '1rem',
    borderRadius: theme.borderRadius.md,
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem'
  },

  alertSuccess: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    border: `1px solid #a7f3d0`
  },

  alertError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: `1px solid #fecaca`
  },

  alertWarning: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    border: `1px solid #fde68a`
  },

  alertInfo: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    border: `1px solid #bfdbfe`
  },

  // ============ UTILITÁRIOS ============
  textCenter: {
    textAlign: 'center'
  },

  textRight: {
    textAlign: 'right'
  },

  textMuted: {
    color: colors.gray
  },

  textSmall: {
    fontSize: '0.875rem'
  },

  textXs: {
    fontSize: '0.75rem'
  },

  fontBold: {
    fontWeight: '600'
  }
};

// Adicionar estilos inline para animações
const globalStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideUp {
    from { transform: translateY(10px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  * {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    font-family: ${theme.fontFamily.sans};
    background-color: ${colors.light};
    color: ${colors.dark};
  }

  a {
    color: ${colors.primary};
    text-decoration: none;
    transition: color 0.2s ease;
  }

  a:hover {
    color: ${colors.primaryDark};
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${theme.fontFamily.heading};
    font-weight: 600;
    margin-top: 0;
  }

  input, select, textarea, button {
    font-family: inherit;
  }
`;

export { styles, globalStyles, colors, theme };

